// animalDraft — the per-animal answers BOTH household forms edit (WEL-173):
// the Animal Addendum form (a current tenant adding an animal) and the pre-lease
// intake's animals section (an applicant confirming theirs). One draft shape,
// one set of rules, one field component (components/AnimalQuestionFields.tsx),
// so the two forms cannot drift into asking different things.
//
// DEPENDENCY-FREE (the only import is the equally dependency-free question
// matrix), so every rule here is unit-testable with a bare `npx tsx --test`.
//
// What is asked comes from lib/animalQuestions.ts: pick what kind of animal it
// is (Dog, Cat, Bird, Small caged animal, Reptile, Fish / aquarium, Something
// else), then answer only that kind's questions. Answers to questions the kind
// is not asked stay in the draft — switching the kind back restores them — but
// never reach the wire.

import {
  animalGapPrompt,
  animalKindChoiceOf,
  animalQuestionKind,
  animalTypeForChoice,
  asksAnimalField,
  isAnimalAgeUnit,
  missingAnimalAnswers,
  type AnimalAgeUnit,
  type AnimalAnswers,
  type AnimalField,
  type AnimalGap,
  type AnimalKindChoice,
  type AnimalQuestionKind,
  type OtherAnimalKind,
} from './animalQuestions';

/** One animal's answers as the form holds them: strings for inputs, a
 *  tri-state boolean for spayed/neutered. */
export interface AnimalAnswerDraft {
  name: string;
  /** The one "what kind of animal" choice ('' = not chosen yet). */
  kind: AnimalKindChoice | '';
  otherDescription: string;
  breed: string;
  color: string;
  weightLbs: string;
  age: string;
  ageUnit: AnimalAgeUnit;
  gender: 'male' | 'female' | '';
  spayedNeutered: boolean | null;
  tankGallons: string;
  licenseNumber: string;
  /**
   * Loaded from a stored 'other' animal with no kind (answered before WEL-173,
   * or seeded from AppFolio). Until the household picks a kind it keeps the old
   * "other" questions and answers — so an animal nobody touches (one delegated
   * to someone else, say) goes back to the server exactly as it came — and the
   * form asks what kind it is. Picking a kind supersedes it.
   */
  legacyOther: boolean;
}

/** The answers on the wire (both forms' POST bodies carry these per animal). */
export interface AnimalWireAnswers {
  name?: string;
  type?: 'dog' | 'cat' | 'other';
  otherKind?: OtherAnimalKind;
  otherDescription?: string;
  breed?: string;
  color?: string;
  weightLbs?: number;
  age?: number;
  ageUnit?: AnimalAgeUnit;
  gender?: 'male' | 'female';
  spayedNeutered?: boolean;
  tankGallons?: number;
  licenseNumber?: string;
}

/** A stored animal as the server hands it back (the intake view). */
export interface StoredAnimalAnswers {
  name?: string;
  type?: string;
  otherKind?: string;
  otherDescription?: string;
  breed?: string;
  color?: string;
  weightLbs?: number;
  age?: number;
  ageUnit?: string;
  gender?: 'male' | 'female';
  spayedNeutered?: boolean;
  tankGallons?: number;
  licenseNumber?: string;
}

/** Blank answers (a new card). */
export function blankAnimalAnswers(): AnimalAnswerDraft {
  return {
    name: '',
    kind: '',
    otherDescription: '',
    breed: '',
    color: '',
    weightLbs: '',
    age: '',
    ageUnit: 'years',
    gender: '',
    spayedNeutered: null,
    tankGallons: '',
    licenseNumber: '',
    legacyOther: false,
  };
}

/**
 * A stored animal → draft answers. An 'other' animal stored before WEL-173 has
 * no kind, so its kind choice opens UNANSWERED (the form then asks for it); its
 * old answers are carried in the draft and reappear under whichever kind asks
 * them.
 */
export function storedToAnimalAnswers(a: StoredAnimalAnswers): AnimalAnswerDraft {
  const numText = (n: number | undefined): string => (n !== undefined ? String(n) : '');
  return {
    name: a.name ?? '',
    kind: animalKindChoiceOf(a),
    otherDescription: a.otherDescription ?? '',
    breed: a.breed ?? '',
    color: a.color ?? '',
    weightLbs: numText(a.weightLbs),
    age: numText(a.age),
    ageUnit: isAnimalAgeUnit(a.ageUnit) ? a.ageUnit : 'years',
    gender: a.gender ?? '',
    spayedNeutered: a.spayedNeutered ?? null,
    tankGallons: numText(a.tankGallons),
    licenseNumber: a.licenseNumber ?? '',
    legacyOther: a.type === 'other' && !animalKindChoiceOf(a),
  };
}

/** The stored pair these answers stand for (none until a kind is chosen, or
 *  a bare 'other' for an untouched pre-WEL-173 animal). */
function draftTypePair(
  a: Pick<AnimalAnswerDraft, 'kind' | 'legacyOther'>,
): { type: 'dog' | 'cat' | 'other'; otherKind?: OtherAnimalKind } | undefined {
  if (a.kind) return animalTypeForChoice(a.kind);
  return a.legacyOther ? { type: 'other' } : undefined;
}

/** The question set these answers get (null until a kind is chosen; the old
 *  "other" set for an untouched pre-WEL-173 'other'). */
export function draftQuestionKind(
  a: Pick<AnimalAnswerDraft, 'kind'> & Partial<Pick<AnimalAnswerDraft, 'legacyOther'>>,
): AnimalQuestionKind | null {
  const pair = draftTypePair({ kind: a.kind, legacyOther: a.legacyOther ?? false });
  return pair ? animalQuestionKind(pair) : null;
}

/**
 * Whether the form shows this field. Until a kind is chosen only the name is
 * (the kind choice itself always shows); after, exactly the matrix's questions
 * for that kind.
 */
export function asksDraftField(
  a: Pick<AnimalAnswerDraft, 'kind'> & Partial<Pick<AnimalAnswerDraft, 'legacyOther'>>,
  field: AnimalField,
): boolean {
  const kind = draftQuestionKind(a);
  if (kind === null) return field === 'name';
  return asksAnimalField(kind, field);
}

/** Parse a user-typed number, dropping anything that is not a finite number. */
export function parseAnimalNumber(raw: string): number | undefined {
  const t = raw.trim();
  if (!t) return undefined;
  const n = Number.parseFloat(t);
  return Number.isFinite(n) ? n : undefined;
}

/** The draft's answers, parsed, as the shared matrix judges them. */
export function draftToAnimalAnswers(a: AnimalAnswerDraft): AnimalAnswers {
  return {
    ...draftTypePair(a),
    name: a.name,
    otherDescription: a.otherDescription,
    breed: a.breed,
    color: a.color,
    weightLbs: parseAnimalNumber(a.weightLbs),
    age: parseAnimalNumber(a.age),
    gender: a.gender,
    spayedNeutered: a.spayedNeutered,
    tankGallons: parseAnimalNumber(a.tankGallons),
    licenseNumber: a.licenseNumber,
  };
}

/**
 * Why these animals cannot be submitted yet — one plain sentence per missing
 * answer ("Tell us your dog's breed.", "How big is the tank, in gallons?"), in
 * form order. Empty ⇒ complete. A missing license is never a reason.
 */
export function animalAnswerReasons(animals: readonly AnimalAnswerDraft[]): string[] {
  const reasons: string[] = [];
  animals.forEach((a, index) => {
    const answers = draftToAnimalAnswers(a);
    for (const gap of missingAnimalAnswers(answers)) {
      reasons.push(animalGapPrompt(gap, answers, { index, total: animals.length }));
    }
  });
  return reasons;
}

/**
 * The answers to send for one animal: ONLY what its kind is asked, trimmed and
 * parsed; blanks and unparseable numbers left out; the age's unit sent with the
 * age. A type is sent only once a kind is chosen — or, for an untouched
 * pre-WEL-173 'other', as the bare 'other' it was stored as.
 */
export function draftToWireAnswers(a: AnimalAnswerDraft): AnimalWireAnswers {
  const asks = (field: AnimalField): boolean => asksDraftField(a, field);
  const text = (field: AnimalField, raw: string): string | undefined =>
    asks(field) && raw.trim() ? raw.trim() : undefined;
  const number = (field: AnimalField, raw: string): number | undefined =>
    asks(field) ? parseAnimalNumber(raw) : undefined;
  const pair = draftTypePair(a);
  const name = text('name', a.name);
  const otherDescription = text('otherDescription', a.otherDescription);
  const breed = text('breed', a.breed);
  const color = text('color', a.color);
  const weightLbs = number('weightLbs', a.weightLbs);
  const age = number('age', a.age);
  const tankGallons = number('tankGallons', a.tankGallons);
  const licenseNumber = text('licenseNumber', a.licenseNumber);
  return {
    ...(name !== undefined ? { name } : {}),
    ...(pair ? { type: pair.type } : {}),
    ...(pair?.otherKind ? { otherKind: pair.otherKind } : {}),
    ...(otherDescription !== undefined ? { otherDescription } : {}),
    ...(breed !== undefined ? { breed } : {}),
    ...(color !== undefined ? { color } : {}),
    ...(weightLbs !== undefined ? { weightLbs } : {}),
    ...(age !== undefined ? { age, ageUnit: a.ageUnit } : {}),
    ...(asks('gender') && a.gender ? { gender: a.gender } : {}),
    ...(asks('spayedNeutered') && a.spayedNeutered !== null
      ? { spayedNeutered: a.spayedNeutered }
      : {}),
    ...(tankGallons !== undefined ? { tankGallons } : {}),
    ...(licenseNumber !== undefined ? { licenseNumber } : {}),
  };
}

/** What each missing answer is called in an operator's list. */
function staffFieldWord(gap: AnimalGap, kind: AnimalQuestionKind | null): string {
  switch (gap) {
    case 'otherDescription':
      if (kind === 'bird' || kind === 'reptile') return 'species';
      if (kind === 'something_else') return 'description';
      return 'kind';
    case 'weightLbs':
      return 'weight';
    case 'tankGallons':
      return 'tank size';
    case 'spayedNeutered':
      return 'spay/neuter answer';
    case 'licenseNumber':
      return 'license number';
    case 'gender':
      return 'sex';
    default:
      return gap;
  }
}

function joinWords(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`;
}

/**
 * The same required answers the household forms insist on, in an OPERATOR's
 * words for the staff "record on behalf" form's "Still needed…" line — one item
 * per animal: "Rex's breed, weight and age", "what kind of animal animal 2 is".
 * Staff-entered animals are held to what household-entered ones are (WEL-173).
 */
export function staffAnimalGaps(animals: readonly AnimalAnswerDraft[]): string[] {
  const items: string[] = [];
  animals.forEach((a, index) => {
    const who = a.name.trim() || `animal ${index + 1}`;
    const gaps = missingAnimalAnswers(draftToAnimalAnswers(a));
    if (gaps.length === 0) return;
    if (gaps[0] === 'type' || gaps[0] === 'otherKind') {
      items.push(`what kind of animal ${who} is`);
      return;
    }
    const kind = draftQuestionKind(a);
    items.push(`${who}’s ${joinWords(gaps.map((g) => staffFieldWord(g, kind)))}`);
  });
  return items;
}

/** The missing answers of one animal, for highlighting its inputs. */
export function animalDraftGaps(a: AnimalAnswerDraft): Set<AnimalGap> {
  return new Set(missingAnimalAnswers(draftToAnimalAnswers(a)));
}
